<template>
  <!-- ============================================================
       No.23 アンケート機能：管理者側「アンケート一覧」（Phase 2：複数回対応・A案）
       年度＋助成種別＋回セレクタの3軸。回管理パネル＋回モーダルを同画面に集約。
       設問テーブルは選択中の回スコープ。
       ============================================================ -->
  <div class="CmsSurveyList">
    <div class="contentsWidth mx-auto mt-4">
      <div>
        <p class="title mb-0">アンケート - 一覧</p>
        <hr class="title">
      </div>

      <!-- 絞り込み（年度＋助成種別＋回の3軸） -->
      <div id="serchWrap" class="mt-4">
        <div class="bold">絞り込み条件</div>
        <div class="search_area flex flexLeft">
          <div class="searchYear inlineBlock searchSpace">
            <div>年度</div>
            <div>
              <b-form-select v-model="search.year" :options="yearOptions" @change="fetchRounds">
                <template v-slot:first>
                  <option :value="null">-- 選択してください。 --</option>
                </template>
              </b-form-select>
            </div>
          </div>
          <div class="searchSeries inlineBlock searchSpace">
            <div>助成種別</div>
            <div>
              <b-form-select v-model="search.series" :options="seriesOptions" @change="fetchRounds">
                <template v-slot:first>
                  <option :value="null">-- 選択してください。 --</option>
                </template>
              </b-form-select>
            </div>
          </div>
          <div class="searchRound inlineBlock">
            <div>回（実施回）</div>
            <div>
              <b-form-select
                v-model="selectedRoundId"
                :options="roundOptions"
                :disabled="noInput || rounds.length < 1"
                @change="fetchSurveys">
                <template v-slot:first>
                  <option :value="null" v-if="rounds.length < 1">-- 回がありません --</option>
                </template>
              </b-form-select>
            </div>
          </div>
        </div>
      </div>

      <!-- 年度・助成種別が未選択の場合は案内のみ -->
      <div v-if="noInput" class="contentsWidth mx-auto mt-5">
        <p class="text-center">年度と助成種別を選択してください。</p>
      </div>

      <div v-else class="contentsWidth">
        <!-- 選択中の回への操作（絞り込み枠の直下・検索条件とは分ける） -->
        <div class="flex flexMiddle mb-2">
          <b-button size="sm" variant="outline-primary" class="mr-2" @click="openRoundModal('add')">
            ＋ 回を追加
          </b-button>
          <b-button
            size="sm" variant="outline-secondary" class="mr-2"
            :disabled="!selectedRound"
            @click="openRoundModal('edit')">
            回を編集
          </b-button>
          <b-button
            size="sm" variant="outline-danger"
            :disabled="!selectedRound || !selectedRound.deletable"
            @click="removeRound">
            回を削除
          </b-button>
          <span v-if="rounds.length < 1" class="text-muted small ml-3">
            回がありません。「＋回を追加」から作成してください。
          </span>
          <span v-else-if="selectedRound && !selectedRound.deletable" class="text-muted small ml-3">
            ※回答が存在するため、この回は削除できません。
          </span>
        </div>

        <!-- 選択中の回の状態 -->
        <div v-if="selectedRound" class="text-muted small mb-3">
          回答期間：{{ periodLabel(selectedRound) }}
          ／ 状態：{{ stateLabel(selectedRound.state) }}
          ／ 回答者数：{{ selectedRound.answer_user_count }}人
          <span v-if="lockLabel" class="badge badge-secondary ml-2">{{ lockLabel }}</span>
        </div>

        <!-- 選択中の回の設問一覧 -->
        <template v-if="selectedRound">
          <b-alert :show="!roundEditable" variant="warning" class="mb-3">
            {{ lockMessage }}
          </b-alert>

          <div class="flex flexRight my-2">
            <b-button variant="primary" class="bold mr-2"
              :disabled="surveys.length < 1"
              @click="openPreview">
              プレビュー
            </b-button>
            <b-button variant="primary" class="bold mr-2"
              :disabled="selectedIds.length < 1"
              @click="openCopyModal">
              設問をコピー
            </b-button>
            <b-button variant="primary" class="bold"
              :disabled="!roundEditable"
              @click="goCreate">
              ＋ 設問を追加
            </b-button>
          </div>

          <b-table striped hover
            table-class="cmsSurveyTable"
            thead-class="tableHead"
            :items="surveys"
            :fields="header"
            show-empty>
            <template v-slot:empty>
              <div class="flex flexCenter">設問がありません。「設問を追加」から作成してください。</div>
            </template>

            <!-- コピー対象の選択（申請管理一覧と同じ作法。この一覧はページングが無いため項目は2つ） -->
            <template v-slot:head(checkbox)>
              <b-link
                id="surveySelectPopover"
                class="ml-1">全選択/全解除</b-link>
              <b-popover
                target="surveySelectPopover"
                title=""
                triggers="hover"
                placement="right">
                <template v-slot:default>
                  <p class="mb-0 link" @click="allSelect">全選択</p>
                  <p class="mb-0 link" @click="allRemove">全解除</p>
                </template>
              </b-popover>
            </template>

            <template v-slot:cell(checkbox)="row">
              <div class="flex flexCenter">
                <b-form-checkbox
                  @change="updateSelectedIds($event, row.item.id)"
                  :checked="selectedIds"
                  :value="row.item.id"
                  />
              </div>
            </template>

            <template v-slot:cell(no)="row">
              {{ row.index + 1 }}
            </template>

            <template v-slot:cell(question_text)="row">
              <span class="pre-wrap">{{ row.item.question_text }}</span>
            </template>

            <template v-slot:cell(input_type)="row">
              {{ typeLabel(row.item.input_type) }}
            </template>

            <template v-slot:cell(is_required)="row">
              <span v-if="row.item.is_required" class="badge badge-required">必須</span>
              <span v-else class="text-muted">任意</span>
            </template>

            <template v-slot:cell(actions)="row">
              <b-link @click="goEdit(row.item.id)" class="mr-3">{{ roundEditable ? '編集' : '詳細' }}</b-link>
              <template v-if="roundEditable">
                <span
                  class="reorder-btn mr-1"
                  :class="{ disabled: row.index === 0 }"
                  title="上へ"
                  @click="moveUp(row.index)">▲</span>
                <span
                  class="reorder-btn"
                  :class="{ disabled: row.index === surveys.length - 1 }"
                  title="下へ"
                  @click="moveDown(row.index)">▼</span>
              </template>
            </template>
          </b-table>
        </template>
      </div>
    </div>

    <!-- 回の追加/編集モーダル -->
    <b-modal
      id="roundModal"
      :title="roundModalMode === 'add' ? '回を追加' : '回を編集'"
      ok-title="保存"
      cancel-title="キャンセル"
      footer-class="justify-content-center"
      @ok="saveRound">
      <div class="mb-3">
        <div class="bold mb-1"><span class="badge badge-required mr-1">必須</span>回次名</div>
        <b-form-input v-model="roundForm.name" placeholder="例：中間調査"/>
      </div>
      <div class="mb-2">
        <div class="bold mb-1">回答開始日時</div>
        <!--
          時刻セレクタの幅は申請フォーム（cms/form/Date.vue）と同じ ymdWrapClass="w-100px"（100px固定）。
          selectWrapSize は既定 2（=col-sm-2）だとモーダル幅では約78pxしかなく 100px の枠がはみ出すため 3 にする。
        -->
        <v-form-calendar-datetime
          no-label
          itemStyle="line"
          inputClass="w-small"
          selectWrapSize="3"
          ymdWrapClass="w-100px"
          :noMinute="true"
          :canPast="false"
          :value="roundForm.open_datetime"
          @change="onChangeOpen"/>
      </div>
      <div class="mb-2">
        <!-- 終了日時は canPast を付けない（運用中の回の期間延長という正当な操作があるため） -->
        <div class="bold mb-1">回答終了日時</div>
        <v-form-calendar-datetime
          no-label
          itemStyle="line"
          inputClass="w-small"
          selectWrapSize="3"
          ymdWrapClass="w-100px"
          :noMinute="true"
          :value="roundForm.close_datetime"
          @change="onChangeClose"/>
      </div>
      <p class="text-muted small mb-0">
        ※回答期間を未設定にすると採択者には表示されません（回答期間を設定すると採択者に配信されます）。
      </p>
    </b-modal>

    <!-- 設問のコピー（選択した設問をコピー先の回の末尾に追加する） -->
    <b-modal
      id="surveyCopyModal"
      title="設問をコピー"
      size="lg"
      ok-title="コピー"
      cancel-title="キャンセル"
      footer-class="justify-content-center"
      @ok="execCopy">
      <p>
        選択した <strong>{{ selectedIds.length }}件</strong> の設問を、選択したコピー先の回の<strong>末尾に追加</strong>します。
      </p>
      <div class="bold mb-1">コピー先</div>
      <div class="mb-2">
        <div class="small mb-1">年度</div>
        <b-form-select v-model="copyForm.dstYear" :options="yearOptions" @change="fetchCopyDstRounds"/>
      </div>
      <div class="mb-2">
        <div class="small mb-1">助成種別</div>
        <b-form-select v-model="copyForm.dstSeries" :options="seriesOptions" @change="fetchCopyDstRounds"/>
      </div>
      <div class="mb-3">
        <div class="small mb-1">回（実施回）</div>
        <b-form-select
          v-model="copyForm.dstRoundId"
          :options="copyDstRoundOptions"
          :disabled="copyDstRounds.length < 1">
          <template v-slot:first>
            <option :value="null" v-if="copyDstRounds.length < 1">
              -- 回がありません。先に「＋ 回を追加」で作成してください --
            </option>
          </template>
        </b-form-select>
      </div>
      <p class="text-muted small mb-0">
        ※<strong>回答期間中の回・回答がある回</strong>は設問を変更できないため、コピー先に選べません（グレー表示）。<br>
        ※選択した設問と選択肢が複製されます（回答は複製されません）。<br>
        ※コピー先の既存の設問はそのまま残り、末尾に追加されます。
      </p>
    </b-modal>

    <!-- プレビュー（報告者の報告画面と同じ表示） -->
    <b-modal
      id="surveyPreviewModal"
      title="アンケート プレビュー（報告画面での表示）"
      dialog-class="surveyPreviewDialog"
      ok-only
      ok-title="閉じる"
      footer-class="justify-content-center">
      <p class="text-muted small mb-2">
        ※報告者と同じように操作できます（選択肢を開く・入力するなど）。
        <strong>入力した内容は保存されません</strong>（閉じると破棄されます）。
      </p>
      <v-app-survey
        :surveys="previewSurveys"
        v-model="previewAnswers"
        :writable="true"
        :errors="{}"/>
    </b-modal>
  </div>
</template>

<script>
import api from '@/modules/api';
import surveyConst from '@/constants/survey';
import Survey from '@/components/parts/report/Survey.vue';

export default {
  name: 'cmsSurveysList',
  components: {
    'v-app-survey': Survey,
  },
  data() {
    return {
      search: {
        year: null,
        series: null,
      },
      yearList: [],
      seriesList: [],
      // 実施回（回）
      rounds: [],
      selectedRoundId: null,
      roundModalMode: 'add',
      roundForm: {
        roundId: null,
        name: '',
        open_datetime: null,
        close_datetime: null,
      },
      // 設問のコピー（コピー先の指定）
      copyForm: {
        dstYear: null,
        dstSeries: null,
        dstRoundId: null,
      },
      copyDstRounds: [],
      // コピー対象として選択した設問（一過性の操作のため store は使わない）
      selectedIds: [],
      // 選択中の回の設問
      surveys: [],
      editable: true,
      // プレビュー
      previewSurveys: [],
      previewAnswers: {},
      header: [
        // ヘッダーに「全選択/全解除」リンクを置くため、その文字幅ぶんを確保する
        { key: 'checkbox', label: '', sortable: false, thStyle: { width: '110px' } },
        { key: 'no', label: 'No', thStyle: { width: '52px' } },
        { key: 'question_text', label: '設問' },
        { key: 'input_type', label: '形式', thStyle: { width: '170px' } },
        { key: 'is_required', label: '必須', thStyle: { width: '60px' } },
        { key: 'actions', label: '操作', thStyle: { width: '116px' } },
      ],
    };
  },
  computed: {
    yearOptions() {
      return this.yearList.map(y => ({ value: y, text: String(y) }));
    },
    seriesOptions() {
      return this.seriesList.map(s => ({ value: s.id, text: s.name }));
    },
    noInput() {
      return !this.search.year || !this.search.series;
    },
    roundOptions() {
      return this.rounds.map(r => ({ value: r.id, text: r.name }));
    },
    // コピー先の回。設問を増やす操作のため、設問追加と同じロック条件で選択不可にする。
    // 選択不可の理由はモーダル下部の注記に一本化する（回次名が長いと option の末尾が切れて読めないため）
    copyDstRoundOptions() {
      return this.copyDstRounds.map(r => ({
        value: r.id,
        text: `${r.name}（設問${r.survey_count}件）`,
        disabled: !r.editable,
      }));
    },
    selectedRound() {
      return this.rounds.find(r => r.id === this.selectedRoundId) || null;
    },
    // 選択中の回の設問が編集可能か（状態≠回答期間中 かつ 回答0件）
    roundEditable() {
      return !!(this.selectedRound && this.selectedRound.editable);
    },
    // 設問ロックのバッジ文言（編集可のときは空）
    lockLabel() {
      if (!this.selectedRound || this.selectedRound.editable) {
        return '';
      }
      return this.selectedRound.lock_reason === 'answered' ? '回答あり（設問ロック）' : '回答期間中（設問ロック）';
    },
    // 設問ロックの説明文（解除方法まで案内する）
    lockMessage() {
      if (!this.selectedRound || this.selectedRound.editable) {
        return '';
      }
      if (this.selectedRound.lock_reason === 'answered') {
        return 'この回にはすでに回答があるため、設問の追加・編集・並び替え・削除はできません（閲覧のみ）。設問を変えたい場合は新しい回を作成してください。';
      }
      return 'この回は回答期間中のため、設問の追加・編集・並び替え・削除はできません（閲覧のみ）。修正が必要な場合は、回答開始日時を先の日時に変更すると編集できるようになります。';
    },
  },
  async created() {
    // 通信が終わるまで画面を隠す（App.vue が router-view を v-show で隠す）。
    // これが無いと、データ到着前の空の状態が一瞬見えてちらつく。
    // ★finally で必ず消す：途中で return や例外が起きて消し忘れると
    //   画面が隠れたまま（＝真っ白）になり、ちらつきより悪い壊れ方をする。
    this.$store.dispatch('page/onLoading');
    try {
      await this.fetchInit();
      if (this.$route.query.year) {
        this.search.year = Number(this.$route.query.year);
      }
      if (this.$route.query.series) {
        this.search.series = Number(this.$route.query.series);
      }
      if (!this.noInput) {
        await this.fetchRounds();
        // 編集画面からの戻り時に選択回を復元
        if (this.$route.query.roundId) {
          this.selectedRoundId = Number(this.$route.query.roundId);
          await this.fetchSurveys();
        }
      }
    } finally {
      this.$store.dispatch('page/offLoading');
    }
  },
  methods: {
    typeLabel(type) {
      return surveyConst.SURVEY_INPUT_TYPE_TEXTS[type] || type;
    },
    // 日時の表示（入力が日＋時までのため、分・秒は表示しない）
    datetimeLabel(v) {
      if (!v) {
        return '（未設定）';
      }
      const m = String(v).replace(/-/g, '/').match(/^(\d{4}\/\d{2}\/\d{2}) (\d{2})/);
      return m ? `${m[1]} ${m[2]}時` : v;
    },
    periodLabel(round) {
      if (!round.open_datetime && !round.close_datetime) {
        return '未設定（採択者に非表示）';
      }
      return `${this.datetimeLabel(round.open_datetime)} 〜 ${this.datetimeLabel(round.close_datetime)}`;
    },
    // 回の状態ラベル（採択者側と同じ4状態＝constants.survey.ROUND_STATE）
    stateLabel(state) {
      return {
        hidden: '未配信（回答期間 未設定）',
        before: '回答開始前',
        open: '回答期間中',
        closed: '回答終了',
      }[state] || state;
    },
    async fetchInit() {
      const res = await api.send('/api/cms/survey/init').catch(() => false);
      if (!res) {
        return;
      }
      this.yearList = res.data.yearList || [];
      this.seriesList = res.data.seriesList || [];
      if (this.yearList.length > 0) {
        const [latestYear] = this.yearList;
        this.search.year = latestYear;
      }
    },
    // 年度・助成種別の回一覧を取得（先頭の回を自動選択）
    async fetchRounds() {
      // 通信前に状態を空にするため、覆わないと「消えて出て、また消えて出る」と2回明滅する。
      // 内側で fetchSurveys も呼ぶが、loadStockList が入れ子を数えるので消し忘れにならない。
      this.$store.dispatch('page/onLoading');
      try {
        this.rounds = [];
        this.selectedRoundId = null;
        this.surveys = [];
        this.selectedIds = [];
        if (this.noInput) {
          return;
        }
        const params = { year: this.search.year, series: this.search.series };
        const res = await api.send('/api/cms/survey/rounds', params).catch(() => false);
        if (!res) {
          return;
        }
        this.rounds = res.data.rounds || [];
        if (this.rounds.length > 0) {
          this.selectedRoundId = this.rounds[0].id;
          await this.fetchSurveys();
        }
      } finally {
        this.$store.dispatch('page/offLoading');
      }
    },
    // 選択中の回の設問を取得（回が変わればコピー対象の選択はクリアする）
    async fetchSurveys() {
      this.selectedIds = [];
      if (this.noInput || !this.selectedRoundId) {
        this.surveys = [];
        return;
      }
      const params = { year: this.search.year, series: this.search.series, roundId: this.selectedRoundId };
      const res = await api.send('/api/cms/survey/list', params).catch(() => false);
      if (!res) {
        return;
      }
      this.surveys = res.data.surveys || [];
      this.editable = res.data.editable;
    },
    // 回モーダル
    openRoundModal(mode) {
      this.roundModalMode = mode;
      if (mode === 'edit' && this.selectedRound) {
        this.roundForm = {
          roundId: this.selectedRound.id,
          name: this.selectedRound.name,
          open_datetime: this.selectedRound.open_datetime,
          close_datetime: this.selectedRound.close_datetime,
        };
      } else {
        this.roundForm = { roundId: null, name: '', open_datetime: null, close_datetime: null };
      }
      this.$bvModal.show('roundModal');
    },
    onChangeOpen(param) {
      this.roundForm.open_datetime = param.value;
    },
    onChangeClose(param) {
      this.roundForm.close_datetime = param.value;
    },
    // 送信用に日時を YYYY-MM-DD HH:mm:ss へ正規化（コンポーネントは / 区切りで返す）
    normalizeDatetime(v) {
      return v ? String(v).replace(/\//g, '-') : null;
    },
    async saveRound(bvModalEvt) {
      if (bvModalEvt) {
        bvModalEvt.preventDefault();
      }
      if (!this.roundForm.name || this.roundForm.name.trim() === '') {
        await this.alert('回次名を入力してください。');
        return;
      }
      // 回答がある回の期間変更は採択者に影響するため確認する
      const cur = this.selectedRound;
      const periodChanged = this.roundModalMode === 'edit' && cur
        && (this.normalizeDatetime(this.roundForm.open_datetime) !== this.normalizeDatetime(cur.open_datetime)
          || this.normalizeDatetime(this.roundForm.close_datetime) !== this.normalizeDatetime(cur.close_datetime));
      if (periodChanged && cur.answer_user_count > 0) {
        const ok = await this.confirm('この回にはすでに回答があります。回答期間を変更すると、回答済みの採択者が回答を確認できなくなったり、入力中の内容を保存できなくなる場合があります。変更してよろしいですか？');
        if (!ok) {
          return;
        }
      }
      const params = {
        roundId: this.roundForm.roundId,
        year: this.search.year,
        series: this.search.series,
        name: this.roundForm.name,
        open_datetime: this.normalizeDatetime(this.roundForm.open_datetime),
        close_datetime: this.normalizeDatetime(this.roundForm.close_datetime),
      };
      let res;
      try {
        res = await api.send('/api/cms/survey/round/save', params);
      } catch (e) {
        const errs = (e.response && e.response.data && e.response.data.errs) || ['回の保存に失敗しました。'];
        await this.alert(errs.join('\n'));
        return;
      }
      this.$bvModal.hide('roundModal');
      const newId = res && res.data ? res.data.id : null;
      await this.fetchRounds();
      // 追加/編集した回を選択状態にする
      if (newId && this.rounds.find(r => r.id === newId)) {
        this.selectedRoundId = newId;
        await this.fetchSurveys();
      }
    },
    /**
     * コピー対象の選択。
     * :checked に配列を渡した b-form-checkbox は「配列モード」になり、
     * @change は真偽値ではなく【チェック後の値の配列】を渡してくる（申請管理一覧と同じ作法）。
     * 空配列も truthy なので真偽値として扱うと解除が効かない。
     * また selectedIds は push で破壊的に変えず必ず新しい配列に差し替える（:checked の参照が変わらないと子が追従しないため）。
     */
    updateSelectedIds(event, surveyId) {
      const isChecked = Array.isArray(event) ? event.includes(surveyId) : !!event;
      if (isChecked) {
        if (!this.selectedIds.includes(surveyId)) {
          this.selectedIds = [...this.selectedIds, surveyId];
        }
        return;
      }
      this.selectedIds = this.selectedIds.filter(id => id !== surveyId);
    },
    allSelect() {
      this.selectedIds = this.surveys.map(s => s.id);
    },
    allRemove() {
      this.selectedIds = [];
    },
    // 設問のコピー：初期のコピー先は現在の絞り込み
    async openCopyModal() {
      this.copyForm = {
        dstYear: this.search.year,
        dstSeries: this.search.series,
        dstRoundId: null,
      };
      this.copyDstRounds = [];
      await this.fetchCopyDstRounds();
      this.$bvModal.show('surveyCopyModal');
    },
    // コピー先の年度・助成種別に属する回を取得（コピー可能な先頭の回を自動選択）
    async fetchCopyDstRounds() {
      this.copyDstRounds = [];
      this.copyForm.dstRoundId = null;
      if (!this.copyForm.dstYear || !this.copyForm.dstSeries) {
        return;
      }
      const params = { year: this.copyForm.dstYear, series: this.copyForm.dstSeries };
      const res = await api.send('/api/cms/survey/rounds', params).catch(() => false);
      if (!res) {
        return;
      }
      this.copyDstRounds = res.data.rounds || [];
      // 表示中の回（＝コピー元）とロック中の回は選べないため、最初の選択可能な回を既定にする
      const selectable = this.copyDstRounds.find(r => r.editable && r.id !== this.selectedRoundId);
      this.copyForm.dstRoundId = selectable ? selectable.id : null;
    },
    async execCopy(bvModalEvt) {
      if (bvModalEvt) {
        bvModalEvt.preventDefault();
      }
      if (!this.copyForm.dstRoundId) {
        await this.alert('コピー先の回を選択してください。');
        return;
      }
      if (this.copyForm.dstRoundId === this.selectedRoundId) {
        await this.alert('コピー元とコピー先が同じ回です。別の回を選択してください。');
        return;
      }
      const params = {
        srcRoundId: this.selectedRoundId,
        surveyIds: this.selectedIds,
        dstRoundId: this.copyForm.dstRoundId,
      };
      let res;
      try {
        res = await api.send('/api/cms/survey/copy', params);
      } catch (e) {
        const errs = (e.response && e.response.data && e.response.data.errs) || ['設問のコピーに失敗しました。'];
        await this.alert(errs.join('\n'));
        return;
      }
      this.$bvModal.hide('surveyCopyModal');
      this.selectedIds = [];
      const count = res && res.data ? res.data.count : 0;
      // コピー先は必ず別の回のため、表示中の一覧は変わらない（再取得は不要）
      await this.alert(`${count}件の設問をコピーしました。`, false);
    },
    async removeRound() {
      if (!this.selectedRound) {
        return;
      }
      if (!await this.confirm(`回「${this.selectedRound.name}」を削除します。配下の設問もすべて削除されます。よろしいですか？`)) {
        return;
      }
      try {
        await api.send('/api/cms/survey/round/destroy', { roundId: this.selectedRoundId });
      } catch (e) {
        const errs = (e.response && e.response.data && e.response.data.errs) || ['回の削除に失敗しました。'];
        await this.alert(errs.join('\n'));
        return;
      }
      await this.alert('回を削除しました。', false);
      await this.fetchRounds();
    },
    // プレビュー（選択中の回の設問）
    async openPreview() {
      const params = { year: this.search.year, series: this.search.series, roundId: this.selectedRoundId };
      const res = await api.send('/api/cms/survey/preview', params).catch(() => false);
      if (!res) {
        await this.alert('プレビューの取得に失敗しました。');
        return;
      }
      this.previewSurveys = res.data.surveys || [];
      this.previewAnswers = {};
      this.$bvModal.show('surveyPreviewModal');
    },
    goCreate() {
      this.$router.push({
        path: '/cms/surveys/edit',
        query: { year: this.search.year, series: this.search.series, roundId: this.selectedRoundId },
      });
    },
    goEdit(surveyId) {
      this.$router.push({
        path: '/cms/surveys/edit',
        query: { year: this.search.year, series: this.search.series, roundId: this.selectedRoundId, surveyId },
      });
    },
    async moveUp(i) {
      if (i <= 0) {
        return;
      }
      const [item] = this.surveys.splice(i, 1);
      this.surveys.splice(i - 1, 0, item);
      await this.saveSort();
    },
    async moveDown(i) {
      if (i >= this.surveys.length - 1) {
        return;
      }
      const [item] = this.surveys.splice(i, 1);
      this.surveys.splice(i + 1, 0, item);
      await this.saveSort();
    },
    async saveSort() {
      const surveyIds = this.surveys.map(s => s.id);
      const res = await api.send('/api/cms/survey/sort', { surveyIds }).catch(() => false);
      if (!res) {
        await this.alert('並び替えの保存に失敗しました。画面を再読み込みします。');
        await this.fetchSurveys();
      }
    },
  },
};
</script>

<style scoped>
#serchWrap input, #serchWrap select {
  height: 50px;
}
.search_area {
  background: #DDD;
  padding: 10px;
  margin-bottom: 30px;
}
/* 絞り込み3軸（年度・助成種別・回）を contentsWidth=1080px に収める */
.searchYear {
  width: 135px;
}
.searchSeries {
  width: 420px;
}
.searchRound {
  width: 380px;
}
.searchSpace {
  margin-right: 15px;
  margin-bottom: 5px;
}
/* 表ヘッダー内の「全選択/全解除」リンク（濃色ヘッダー上のため白＋下線・申請管理一覧の #popover と同一） */
#surveySelectPopover {
  cursor: pointer;
  color: #FFF !important;
  text-decoration: underline;
}
/* 全選択/全解除 ポップオーバー内のリンク（申請管理一覧と同一） */
.link {
  color: #0A8EA7 !important;
  cursor: pointer;
}
.link:hover {
  text-decoration: underline;
}
.reorder-btn {
  cursor: pointer;
  color: #0A8EA7;
  user-select: none;
}
.reorder-btn.disabled {
  color: #ccc;
  cursor: default;
  pointer-events: none;
}
.pre-wrap {
  white-space: pre-wrap;
}
</style>
